import React from 'react';
import { AddUsersToGroupGridCellFormatterFactory } from './AddUsersToGroupCellFormatter';
import { CheckboxState } from '../../../common/components/checkboxes/CheckboxState';
import { CommonColumn } from '../../../app/spaces/ListView/types/Columns';
import { DataGrid } from '@tableau/widgets-datagrid';
import { dataGridContainerStyle, dataGridRowHeight, dataGridStyle } from './styles/AddUsersToGroupDialogStyles';
import { DateTimeCell } from '../../../app/spaces/ListView/dataGridCells/DateTimeCells';
import { IOrderClause } from '@tableau/data-catalog-client-components';
import { ISiteUserInfo } from '../../../ts/models/ISiteUserInfo';
import { Messages } from '../../../codegen/Localize';
import { OrderKeys } from '../../../ts/models/OrderKeys';
import { PlainTextCell, PlainTextCellWithTransform } from '../../../app/spaces/ListView/dataGridCells/PlainTextAndNumberCells';
import { SiteRolesHelper } from '../../../ts/helpers/SiteRolesHelper';
import { SortableHeader } from '../../../app/spaces/ListView/SortableHeader';

export enum AddUsersToGroupDataKeys {
  Select = 'selected',
  SiteUserDisplayName = 'displayName',
  SiteUserName = 'username',
  SiteUserRole = 'siteRole',
  SiteUserLastSignIn = 'lastSignIn',
}
export namespace AddUsersToGroupTypes {
  export interface GridProps {
    emptyChild: JSX.Element;
    userList: ISiteUserInfo[];
    inGroupUsersList: ISiteUserInfo[];
    currentColumnSortOrder: IOrderClause;
    handleColumnSortOrderChange: (field: string) => void;
    rowCheckboxState: _.Dictionary<string>;
    handleRowCheckboxChange: (rowData: Partial<ISiteUserInfo>) => void;
    shouldAskForNextPage: (dataGridEndIndex: number) => boolean;
    askForNextPage: () => void;
  }

  export interface GridState {
    selectedUsers: ISiteUserInfo[];
  }

  export interface CheckboxCellData {
    checkboxState: CheckboxState;
    isCheckboxEnabled: boolean;
    onCheckboxChanged: () => void;
  }

  export interface Column extends DataGrid.Column {
    checkboxState?: CheckboxState;
    isCheckboxEnabled?: boolean;
    onCheckboxChanged?: () => void;
  }

  export interface Row extends DataGrid.Row {
  }
}

export class AddUsersToGroupGrid extends React.Component<AddUsersToGroupTypes.GridProps, AddUsersToGroupTypes.GridState> {
  public state: AddUsersToGroupTypes.GridState = {
    selectedUsers: []
  };

  /** Used by DataGrid's rowStore; public for testing purposes */
  public rows: DataGrid.Row[] = [];

  /** Passed into DataGrid's prop.cols */
  private cols: DataGrid.Column[] = [];

  private getRowStore = (): DataGrid.RowStore => {
    return {
      getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {
        rowHandler(this.rows);
        if (this.props.shouldAskForNextPage(end)) {
          this.props.askForNextPage();
        }
      }
    } as DataGrid.RowStore;
  }

  private generateCols = (): DataGrid.Column[] => {
    const sortableHeaderFormatter = SortableHeader.Formatter([this.props.currentColumnSortOrder], this.props.handleColumnSortOrderChange);
    const columns: AddUsersToGroupTypes.Column[] = [];
    columns.push({
      name: CommonColumn.Select,
      dataKey: AddUsersToGroupDataKeys.Select,
      headerText: '',
      resizable: false,
      sortable: false,
      cellFormatter: AddUsersToGroupGridCellFormatterFactory
    });
    columns.push({
      name: CommonColumn.SiteUserDisplayName,
      dataKey: AddUsersToGroupDataKeys.SiteUserDisplayName,
      sortable: true,
      direction: 'asc',
      growToFit: true,
      headerText: Messages.column_fullName(),
      headerFormatter: sortableHeaderFormatter,
      cellFormatter: AddUsersToGroupGridCellFormatterFactory
    });
    columns.push({
      name: CommonColumn.SiteUserName,
      dataKey: AddUsersToGroupDataKeys.SiteUserName,
      sortable: true,
      headerText: Messages.column_username(),
      direction: 'asc',
      headerFormatter: sortableHeaderFormatter,
      cellFormatter: PlainTextCell(OrderKeys.username)
    });
    columns.push({
      name: CommonColumn.SiteUserRole,
      dataKey: AddUsersToGroupDataKeys.SiteUserRole,
      sortable: true,
      headerText: Messages.column_site_role(),
      direction: 'asc',
      headerFormatter: sortableHeaderFormatter,
      cellFormatter: PlainTextCellWithTransform(
        'siteRole',
      SiteRolesHelper.siteRoleToName)
    });
    columns.push({
      name: CommonColumn.SiteUserLastSignin,
      dataKey: OrderKeys.lastSignIn,
      sortable: true,
      headerText: Messages.column_last_sign_in(),
      direction: 'desc',
      headerFormatter: sortableHeaderFormatter,
      cellFormatter: DateTimeCell('lastSignIn')
    });
    return columns;
  }

  private generateRows(): DataGrid.Row[] {
    let rows: AddUsersToGroupTypes.Row[] = [];
    let nextRowIndex: number = 0;
    this.props.userList.forEach(rowData => {
      let rowItemData: { [key: string]: {} } = {};
      const currentRowIndex = nextRowIndex++;
      const userAlreadyInGroup: boolean = this.props.inGroupUsersList.some(userInGroup => {
        return rowData.username === userInGroup.username;
      });
      let checkboxCellData: AddUsersToGroupTypes.CheckboxCellData = {
        checkboxState: userAlreadyInGroup ? CheckboxState.checked : this.props.rowCheckboxState[rowData.id],
        isCheckboxEnabled: !(userAlreadyInGroup),
        onCheckboxChanged: () => { this.props.handleRowCheckboxChange(rowData); }
      };
      rowItemData[AddUsersToGroupDataKeys.Select] = checkboxCellData;
      rowItemData[AddUsersToGroupDataKeys.SiteUserDisplayName] = rowData.displayName;
      rowItemData[AddUsersToGroupDataKeys.SiteUserName] = rowData.username;
      rowItemData[AddUsersToGroupDataKeys.SiteUserRole] = rowData.siteRole;
      rowItemData[AddUsersToGroupDataKeys.SiteUserLastSignIn] = rowData.lastSignIn;
      rows.push({
        key: (currentRowIndex).toString(),
        data: rowItemData
      });
    });
    return rows;
  }



  private generateContent = (): void => {
    this.cols = this.generateCols();
    this.rows = this.generateRows();
  }

  public render(): JSX.Element {
    this.generateContent();
    return <div className={dataGridContainerStyle}>
      <div className={'tb-spaces-list-view'}>
        <DataGrid
          config= {{
            rowOuterHeight: dataGridRowHeight,
            rememberLastScrollPos: true
          }}
          emptyChild={this.props.emptyChild}
          cols={this.cols}
          rowStore={this.getRowStore()}
          showVerticalGridLines={false}
          showHorizontalGridLines={false}
          showRowBanding={false}
          style={dataGridStyle}
        />
      </div>
    </div>;
  }
}